import {Calendar, Target, ChevronRight} from 'lucide-react'
import type {Sprint} from '../../types'
import {useApp} from '../../context/AppContext'
import {getSprintStatusConfig, formatDate} from '../../utils/helpers'
import Badge from '../common/Badge'

interface SprintCardProps {
  sprint: Sprint
  onSelect: (id: string) => void
  isSelected: boolean
}

export default function SprintCard({sprint, onSelect, isSelected}: SprintCardProps) {
  const {getTasksForSprint, state} = useApp()
  const tasks = getTasksForSprint(sprint.id)
  const statusConfig = getSprintStatusConfig(sprint.status)

  const doneTasks = tasks.filter((t) => t.status === 'done').length
  const totalPoints = tasks.reduce((sum, t) => sum + t.storyPoints, 0)
  const donePoints = tasks
    .filter((t) => t.status === 'done')
    .reduce((sum, t) => sum + t.storyPoints, 0)
  const progress = tasks.length > 0 ? Math.round((doneTasks / tasks.length) * 100) : 0

  return (
    <button
      onClick={() => onSelect(sprint.id)}
      className={`devops-card w-full text-left p-4 md:p-5 group ${
        isSelected ? 'bg-brand-50 border-brand-300 ring-1 ring-brand-100' : ''
      }`}
      aria-pressed={isSelected}
      aria-label={`Sprint: ${sprint.name}`}
    >
      <div className='flex items-start justify-between mb-3'>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 mb-1'>
            <h3 className='text-sm font-semibold text-slate-800 truncate'>{sprint.name}</h3>
            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
          </div>
          <p className='text-xs text-slate-500 line-clamp-1'>{sprint.goal}</p>
        </div>
        <ChevronRight
          size={18}
          className={`shrink-0 ml-2 transition-colors ${
            isSelected ? 'text-brand-500' : 'text-slate-300 group-hover:text-brand-400'
          }`}
        />
      </div>

      <p className='text-xs text-slate-400 mb-2'>
        {state.boards.find((board) => board.id === sprint.boardId)?.name || 'Quadro indisponível'}
      </p>
      <div className='flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-3'>
        <span className='flex items-center gap-1'>
          <Calendar size={12} />
          {formatDate(sprint.startDate)} - {formatDate(sprint.endDate)}
        </span>
        <span className='flex items-center gap-1'>
          <Target size={12} />
          {totalPoints} pts
        </span>
      </div>

      <div className='space-y-1.5'>
        <div className='flex items-center justify-between text-xs'>
          <span className='text-slate-500'>
            {doneTasks} / {tasks.length} tarefas
          </span>
          <span className='font-medium text-slate-600'>{progress}%</span>
        </div>
        <div className='devops-progress-track'>
          <div
            className='devops-progress-bar transition-all duration-500 ease-out'
            style={{
              width: `${progress}%`,
              backgroundColor: progress === 100 ? '#10b981' : '#3570E6',
            }}
          />
        </div>
        {totalPoints > 0 && (
          <p className='text-[10px] text-slate-400'>
            {donePoints} / {totalPoints} story points concluídos
          </p>
        )}
      </div>
    </button>
  )
}
